import { useState } from 'react';
import { IconAlertCircle, IconEye, IconEyeOff } from '@tabler/icons-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api, UnauthorizedError } from './api.js';

export function Login({ onSuccess }: { onSuccess: (user: string) => void }) {
  const [user, setUser] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      const res = await api.login(user.trim(), password);
      onSuccess(res.user);
    } catch (err) {
      // UnauthorizedError = backend returned 401 (credential reject).
      // Anything else (network down, CORS, 5xx) is a transport
      // failure — calling those "invalid credentials" misleads the
      // user into retrying with the same password.
      if (err instanceof UnauthorizedError) {
        setError('Invalid credentials.');
      } else if (err instanceof Error) {
        setError(`Login failed: ${err.message}`);
      } else {
        setError('Login failed.');
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center gap-1">
          <h1 className="text-xl font-semibold tracking-tight text-foreground">
            Theia Dashboard
          </h1>
          <p className="text-xs text-muted-foreground">
            Sign in with your Perforce credentials.
          </p>
        </div>
        <Card>
          <CardContent className="pt-5">
            <form onSubmit={submit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="login-user">User</Label>
                <Input
                  id="login-user"
                  value={user}
                  onChange={(e) => setUser(e.currentTarget.value)}
                  autoComplete="username"
                  required
                  disabled={pending}
                  autoFocus
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="login-password">Password</Label>
                <div className="relative">
                  <Input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.currentTarget.value)}
                    autoComplete="current-password"
                    required
                    disabled={pending}
                    className="pr-8"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-0 flex w-7 items-center justify-center text-muted-foreground hover:text-foreground"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <IconEyeOff size={14} /> : <IconEye size={14} />}
                  </button>
                </div>
              </div>
              {error && (
                <div
                  role="alert"
                  className="flex items-start gap-2 rounded-sm border border-red-900/50 bg-red-950/30 px-2.5 py-2 text-xs text-red-300"
                >
                  <IconAlertCircle size={14} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              <Button
                type="submit"
                variant="primary"
                size="md"
                disabled={pending}
                className="w-full"
              >
                {pending ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>
          </CardContent>
        </Card>
        <p className="mt-4 text-center text-xs text-muted-foreground">
          No account? Ask your admin to add you.
        </p>
      </div>
    </div>
  );
}
